import { useState, useEffect, useRef, useCallback } from 'react';
import { UI_COLORS } from '../../utils/colors';

/**
 * Debounced search + select for the portal. The parent supplies:
 *  - fetcher(searchText) => Promise<{success, data:[]}>  (already portal-scoped)
 *  - getOptionLabel(item) => string
 *  - getOptionValue(item) => value
 *  - value / onChange(value, item)
 *
 * Fetches an initial page on mount, then re-fetches (debounced 300ms) as the
 * user types. Shows the picked option's label in the closed state.
 */
const PortalSearchSelect = ({
  fetcher,
  getOptionLabel,
  getOptionValue,
  value,
  onChange,
  placeholder = 'Search…',
  noResultsText = 'No results',
  loadingText = 'Loading…',
  unavailable = false,          // endpoint failed → parent renders its own fallback
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const debounceRef = useRef(null);
  const reqRef = useRef(0);
  const boxRef = useRef(null);

  const loadedOnceRef = useRef(false);

  const runFetch = useCallback(async (term) => {
    const reqId = ++reqRef.current;
    setLoading(true);
    try {
      const res = await fetcher(term);
      if (reqId !== reqRef.current) return; // superseded
      setOptions(res && res.success ? (res.data || []) : []);
    } catch (err) {
      console.error('Portal search fetch failed:', err);
      if (reqId === reqRef.current) setOptions([]);
    } finally {
      // A superseded request must not clear the flag the newer one owns.
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [fetcher]);

  // Fetch only while the dropdown is OPEN. The first open loads immediately;
  // subsequent keystrokes are debounced (300ms). A closed picker never fetches,
  // so adding item rows doesn't trigger extra requests.
  useEffect(() => {
    if (!open) return;
    if (!loadedOnceRef.current) {
      loadedOnceRef.current = true;
      runFetch(query.trim());
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runFetch(query.trim()), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, query, runFetch]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Keep the displayed label in sync if value is cleared externally
  useEffect(() => { if (!value) setSelectedLabel(''); }, [value]);

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };

  const pick = (opt) => {
    const v = getOptionValue(opt);
    const label = getOptionLabel(opt);
    setSelectedLabel(label);
    onChange(v, opt);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        disabled={disabled || unavailable}
        value={open ? query : selectedLabel}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        placeholder={selectedLabel || placeholder}
        className="w-full px-3 py-2 border-2 rounded-lg text-sm"
        style={inputStyle}
      />
      {open && !unavailable && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border shadow-lg" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
          {loading ? (
            <div className="px-3 py-2 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>{loadingText}</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>{noResultsText}</div>
          ) : (
            options.map((opt) => {
              const v = getOptionValue(opt);
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => pick(opt)}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  style={{ color: UI_COLORS.TEXT.PRIMARY }}
                >
                  {getOptionLabel(opt)}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default PortalSearchSelect;
